import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { dashboardApi, sitewideApi, pageTasksApi, settingsApi, googleApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { StatCardSkeleton } from '../components/ui/Skeleton';
import { PriorityBadge, StatusBadge, TeamBadge, OverdueBadge } from '../components/ui/Badge';
import ProgressBar from '../components/ui/ProgressBar';
import { FormattedNotes } from '../components/ui/MarkdownNotes';
import { formatAssigneeNames, formatDate, formatDateTime, getOverdueDays, getProgressPercent, normalizeUrlForWebsite, TEAMS } from '../lib/utils';

// ─── New Lead Celebration ─────────────────────────────────────────────────────

const CONFETTI_COLORS = ['#F97316', '#FF6B35', '#3B82F6', '#16A34A', '#8B5CF6', '#EC4899', '#F59E0B', '#14B8A6', '#EF4444'];

const CONFETTI_CSS = `
@keyframes confettiFall {
  0%   { transform: translateY(-30px) rotate(0deg) scaleX(1);   opacity: 1; }
  50%  { scaleX: 0.6; }
  85%  { opacity: 1; }
  100% { transform: translateY(108vh) rotate(var(--spin)) scaleX(0.4); opacity: 0; }
}
@keyframes leadToastIn {
  0%   { transform: translateX(120%) scale(0.9); opacity: 0; }
  60%  { transform: translateX(-6px) scale(1.02); opacity: 1; }
  100% { transform: translateX(0)    scale(1);    opacity: 1; }
}
@keyframes leadToastOut {
  0%   { transform: translateX(0)    opacity: 1; }
  100% { transform: translateX(120%) opacity: 0; }
}
@keyframes pulseRing {
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(1.8); opacity: 0;   }
}
@keyframes numberPop {
  0%   { transform: scale(1);    }
  40%  { transform: scale(1.35); color: #F97316; }
  100% { transform: scale(1);    }
}
`;

function NewLeadCelebration({ lead, onDone }: { lead: any | null; onDone: () => void }) {
  const [leaving, setLeaving] = React.useState(false);

  const particles = React.useMemo(() =>
    Array.from({ length: 90 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      left: `${(Math.random() * 115) - 7}%`,
      delay: `${(Math.random() * 0.7).toFixed(2)}s`,
      duration: `${(1.4 + Math.random() * 1.1).toFixed(2)}s`,
      size: `${Math.round(5 + Math.random() * 9)}px`,
      spin: `${Math.round((Math.random() > 0.5 ? 1 : -1) * (200 + Math.random() * 520))}deg`,
      circle: Math.random() > 0.45,
    })), []);

  React.useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 4200);
    const doneTimer  = setTimeout(onDone, 5000);
    return () => { clearTimeout(leaveTimer); clearTimeout(doneTimer); };
  }, [onDone]);

  return (
    <>
      <style>{CONFETTI_CSS}</style>

      {/* Confetti particles */}
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'fixed', left: p.left, top: '-30px',
          width: p.size, height: p.size,
          backgroundColor: p.color,
          borderRadius: p.circle ? '50%' : '2px',
          animation: `confettiFall ${p.duration} ${p.delay} cubic-bezier(0.25, 0.1, 0.75, 0.9) forwards`,
          '--spin': p.spin,
          zIndex: 9998, pointerEvents: 'none',
        } as React.CSSProperties} />
      ))}

      {/* Lead toast */}
      <div style={{
        position: 'fixed', bottom: '28px', right: '24px', zIndex: 9999,
        maxWidth: '340px', width: 'calc(100vw - 48px)',
        animation: leaving
          ? 'leadToastOut 0.5s ease forwards'
          : 'leadToastIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        pointerEvents: 'none',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #18181B 0%, #1c1c1f 100%)',
          borderRadius: '18px', padding: '18px 20px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(249,115,22,0.4), 0 0 40px rgba(249,115,22,0.15)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ position: 'relative', width: '44px', height: '44px', flexShrink: 0 }}>
              {/* Pulse ring */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: '2px solid #F97316',
                animation: 'pulseRing 1.2s ease-out infinite',
              }} />
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #F97316, #EA580C)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px',
              }}>🎯</div>
            </div>
            <div>
              <p style={{ color: '#F97316', fontWeight: 800, fontSize: '13px', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                New Lead!
              </p>
              <p style={{ color: '#52525B', fontSize: '11px', margin: '2px 0 0', fontWeight: 500 }}>
                Just arrived in your inbox
              </p>
            </div>
          </div>

          {/* Lead info */}
          {lead && (
            <div style={{
              background: 'rgba(255,255,255,0.05)', borderRadius: '10px',
              padding: '10px 12px', border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <p style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '12px', margin: '0 0 6px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lead.subject || 'New Contact Form Submission'}
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{
                  background: 'rgba(249,115,22,0.2)', color: '#FB923C',
                  fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                  border: '1px solid rgba(249,115,22,0.3)',
                }}>
                  {lead.source || 'gmail'}
                </span>
                <span style={{ color: '#71717A', fontSize: '11px' }}>{lead.website || 'Tkxel'}</span>
                <span style={{ color: '#3F3F46', fontSize: '11px', marginLeft: 'auto' }}>
                  {formatDateTime(lead.received_at)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Email text → table parser ───────────────────────────────────────────────
// Pardot / collect.chat emails arrive as plain text like:
// "First Name: Johnny Last Name: Djurslev Email: jdj@... Phone: 123..."
// This splits on "Field Name: value" boundaries and returns rows.

function parseEmailFields(text: string): { field: string; value: string }[] {
  if (!text || text.trim().length === 0) return [];

  // Match "Word(s): " where the field name starts with a capital letter
  // Negative lookahead avoids splitting on URLs (http://, https://)
  const FIELD_RE = /([A-Z][A-Za-z0-9_ ]{0,50}?):\s+(?!\/\/)/g;

  const positions: { field: string; start: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = FIELD_RE.exec(text)) !== null) {
    positions.push({ field: m[1].trim(), start: m.index + m[0].length });
  }

  if (positions.length === 0) return [{ field: 'Message', value: text.trim() }];

  return positions.map((pos, i) => {
    const end = i + 1 < positions.length
      ? text.lastIndexOf(positions[i + 1].field, positions[i + 1].start) - 1
      : text.length;
    const value = text.slice(pos.start, Math.max(pos.start, end)).trim();
    return { field: pos.field, value };
  }).filter(r => r.value.length > 0 && r.value !== r.field);
}

// ─── Slide Panel (Ahrefs-style right drawer) ─────────────────────────────────

function SlidePanel({
  open,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[520px] bg-white flex flex-col transition-transform duration-300 ease-out`}
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E4E7] flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-[#18181B]">{title}</h2>
            {subtitle && <p className="text-xs text-[#71717A] mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5] hover:text-[#18181B] transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </div>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEstimate(task: any) {
  const h = Number(task.estimated_hours || 0);
  const m = Number(task.estimated_minutes || 0);
  if (h === 0 && m === 0) return null;
  return `${h > 0 ? `${h}h ` : ''}${m > 0 ? `${m}m` : ''}`.trim();
}
function minutesFor(task: any) {
  return Number(task.estimated_hours || 0) * 60 + Number(task.estimated_minutes || 0);
}
function formatTotalEstimate(minutes: number) {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${h > 0 ? `${h}h ` : ''}${rest > 0 ? `${rest}m` : ''}`.trim();
}
function taskSourceLabel(task: any) {
  return task.source === 'page' ? 'Page' : 'Sitewide';
}
function getPeriodRange(period: string, anchor: string) {
  const safe = anchor || new Date().toISOString().slice(0, 10);
  const [year, month] = safe.split('-').map(Number);
  if (period === 'year') return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (period === 'month') {
    const last = new Date(year, month, 0).getDate();
    return { from: `${year}-${String(month).padStart(2, '0')}-01`, to: `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}` };
  }
  return { from: safe, to: safe };
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({
  title, value, subtitle, icon, iconBg, accent, onClick,
}: {
  title: string; value: number | string; subtitle?: string;
  icon: React.ReactNode; iconBg: string; accent?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl p-5 flex flex-col gap-3 transition-all duration-150 hover:shadow-md active:scale-[0.98]"
      style={{
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: accent ? '1px solid #FED7AA' : '1px solid #E4E4E7',
        backgroundColor: accent ? '#FFF7ED' : '#FFFFFF',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
          {icon}
        </div>
        {onClick && (
          <svg className="w-4 h-4 text-[#A1A1AA]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
      <div>
        <p className="text-[30px] font-extrabold leading-none tracking-tight text-[#18181B] tabular-nums">{value}</p>
        <p className="text-sm font-medium text-[#71717A] mt-1">{title}</p>
        {subtitle && <p className="text-xs text-[#A1A1AA] mt-0.5">{subtitle}</p>}
      </div>
    </button>
  );
}

// ─── Stacked Bar ─────────────────────────────────────────────────────────────

function StackedBar({ done, inProgress, pending, blocked, total }: {
  done: number; inProgress: number; pending: number; blocked: number; total: number;
}) {
  if (total === 0) return <div className="h-3 bg-[#E4E4E7] rounded-full" />;
  return (
    <div className="h-3 rounded-full overflow-hidden flex" style={{ backgroundColor: '#E4E4E7' }}>
      {done > 0 && <div style={{ width: `${(done / total) * 100}%`, backgroundColor: '#16A34A' }} title={`Done: ${done}`} />}
      {inProgress > 0 && <div style={{ width: `${(inProgress / total) * 100}%`, backgroundColor: '#2563EB' }} title={`In Progress: ${inProgress}`} />}
      {pending > 0 && <div style={{ width: `${(pending / total) * 100}%`, backgroundColor: '#F97316' }} title={`Pending: ${pending}`} />}
      {blocked > 0 && <div style={{ width: `${(blocked / total) * 100}%`, backgroundColor: '#DC2626' }} title={`Blocked: ${blocked}`} />}
    </div>
  );
}

function LegendItem({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="text-xs text-[#71717A]">{label}</span>
      <span className="text-xs font-bold text-[#18181B] tabular-nums">{count}</span>
    </div>
  );
}

// ─── Task Row (used inside slide panel) ──────────────────────────────────────

function TaskRow({ task, onOpen }: { task: any; onOpen: (t: any) => void }) {
  const overdueDays = getOverdueDays(task.due_date, task.status);

  return (
    <div
      className={`rounded-lg border ${overdueDays ? 'border-red-200' : 'border-[#E4E4E7]'} px-4 py-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors`}
      onClick={() => onOpen(task)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#18181B] truncate">{task.name}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="text-[11px] text-[#A1A1AA] bg-[#F4F4F5] px-1.5 py-0.5 rounded">{taskSourceLabel(task)}</span>
            {task.website_name && <span className="text-[11px] font-semibold text-[#52525B]">{task.website_name}</span>}
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
            {overdueDays && <OverdueBadge days={overdueDays} />}
            {task.due_date && <span className="text-[11px] text-[#71717A]">Due {formatDate(task.due_date)}</span>}
            {formatEstimate(task) && <span className="text-[11px] font-semibold text-[#52525B]">{formatEstimate(task)}</span>}
          </div>
          {formatAssigneeNames(task) && (
            <p className="text-[11px] text-[#A1A1AA] mt-1">→ {formatAssigneeNames(task)}</p>
          )}
        </div>
        <svg className="w-4 h-4 text-[#A1A1AA] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Task Detail View (inside panel) ─────────────────────────────────────────

function DetailField({ label, value, children }: { label: string; value?: React.ReactNode; children?: React.ReactNode }) {
  const content = children ?? value;
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA]">{label}</p>
      <div className="mt-1 text-sm font-medium text-[#18181B] break-words">
        {content || <span className="text-[#A1A1AA]">—</span>}
      </div>
    </div>
  );
}

function TaskDetail({ task, onBack }: { task: any; onBack: () => void }) {
  const pageUrl = normalizeUrlForWebsite(task.page_url, task.website_url);
  const overdueDays = getOverdueDays(task.due_date, task.status);
  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-[#71717A] hover:text-[#18181B] mb-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-[#F4F4F5] px-2.5 py-1 text-xs font-semibold text-[#52525B]">{taskSourceLabel(task)}</span>
        <StatusBadge status={task.status} />
        <PriorityBadge priority={task.priority} />
        {overdueDays && <OverdueBadge days={overdueDays} />}
        {task.website_name && <span className="rounded-full bg-[#F4F4F5] px-2.5 py-1 text-xs font-semibold text-[#52525B]">{task.website_name}</span>}
      </div>

      <h3 className="text-base font-bold text-[#18181B] leading-snug">{task.name}</h3>

      <div className="grid grid-cols-2 gap-2">
        <DetailField label="Website" value={task.website_name || 'Tkxel'} />
        <DetailField label="Assigned To" value={formatAssigneeNames(task)} />
        <DetailField label="Follow-up" value={task.follow_up_by} />
        <DetailField label="Team"><TeamBadge team={task.assigned_team} /></DetailField>
        <DetailField label="Created By" value={task.created_by || 'Unknown'} />
        <DetailField label="Due Date" value={formatDate(task.due_date)} />
        <DetailField label="Time Estimate" value={formatEstimate(task) || '—'} />
        <DetailField label="Created" value={formatDateTime(task.created_at)} />
      </div>

      {task.source === 'page' && (
        <div className="grid grid-cols-2 gap-2">
          <DetailField label="Page Type" value={task.page_type_name || 'Other'} />
          <DetailField label="Task Type" value={task.task_type || task.name} />
          {task.page_url && (
            <div className="col-span-2">
              <DetailField label="Page URL">
                <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline underline-offset-2 text-xs break-all hover:text-blue-800">
                  {task.page_url}
                </a>
              </DetailField>
            </div>
          )}
        </div>
      )}

      {task.description && (
        <div>
          <p className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider mb-1.5">Description</p>
          <div className="rounded-lg border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#3F3F46] leading-relaxed">{task.description}</div>
        </div>
      )}
      {task.notes && (
        <div>
          <p className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider mb-1.5">Notes</p>
          <div className="rounded-lg border border-[#E4E4E7] bg-white px-3 py-2">
            <FormattedNotes value={task.notes} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Member summary (for members only) ───────────────────────────────────────

function MyTasksSummary({ teamMemberName }: { teamMemberName: string }) {
  const { data: swData } = useQuery({ queryKey: ['sitewide-tasks', 'member-visible-summary'], queryFn: () => sitewideApi.list() });
  const { data: ptData } = useQuery({ queryKey: ['page-tasks', 'member-visible-summary'], queryFn: () => pageTasksApi.list() });
  const all = [...(swData?.data || []), ...(ptData?.data || [])];
  const counts = {
    pending: all.filter((t: any) => t.status === 'Pending').length,
    inProgress: all.filter((t: any) => t.status === 'In Progress').length,
    blocked: all.filter((t: any) => t.status === 'Blocked').length,
    done: all.filter((t: any) => t.status === 'Done').length,
  };
  return (
    <div className="rounded-xl p-5" style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F97316' }}>
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div>
          <h2 className="text-base font-bold text-[#18181B]">My Tasks — {teamMemberName}</h2>
          <p className="text-xs text-[#92400E]">{all.length} tasks visible to you</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Pending', value: counts.pending, color: '#6B7280' },
          { label: 'In Progress', value: counts.inProgress, color: '#3B82F6' },
          { label: 'Blocked', value: counts.blocked, color: '#EF4444' },
          { label: 'Done', value: counts.done, color: '#22C55E' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-lg px-4 py-3 text-center" style={{ border: '1px solid #E4E4E7' }}>
            <p className="text-2xl font-extrabold tabular-nums" style={{ color }}>{value}</p>
            <p className="text-xs text-[#71717A] mt-0.5 font-medium">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Panel content types ──────────────────────────────────────────────────────

type PanelView =
  | { type: 'task-list'; title: string; subtitle: string; tasks: any[] }
  | { type: 'task-detail'; task: any; from?: PanelView }
  | { type: 'member-tasks'; member: string; tasks: any[] }
  | { type: 'completed'; tasks: any[]; period: string; anchor: string }
  | { type: 'leads'; stats: any }
  | { type: 'leads-list'; period: string; label: string }
  | { type: 'lead-detail'; leadId: number; from: PanelView }
  | null;

// ─── Email Body Table ─────────────────────────────────────────────────────────

function EmailBodyTable({ text }: { text: string }) {
  const rows = parseEmailFields(text);

  // Group rows into sections — detect section headers like "Full Prospect Details", "Form Handler Submission"
  const SECTION_HEADERS = [
    'form handler submission',
    'full prospect details',
    'view in pardot',
    'recent activity',
    'recent activities',
  ];

  type Row = { type: 'header'; title: string } | { type: 'row'; field: string; value: string };
  const grouped: Row[] = [];

  for (const r of rows) {
    const fieldLower = r.field.toLowerCase();
    if (SECTION_HEADERS.some(h => fieldLower.includes(h.split(' ')[0]) && fieldLower.length > 8)) {
      // Treat as section header (field is a section title, value is sub-content)
      grouped.push({ type: 'header', title: r.field + (r.value && !r.value.startsWith('http') ? ': ' + r.value : '') });
    } else {
      grouped.push({ type: 'row', field: r.field, value: r.value });
    }
  }

  return (
    <div className="rounded-xl border border-[#E4E4E7] overflow-hidden max-h-[500px] overflow-y-auto">
      <table className="w-full text-sm border-collapse">
        <tbody>
          {grouped.map((item, i) => {
            if (item.type === 'header') {
              return (
                <tr key={i}>
                  <td colSpan={2} className="px-4 py-2.5 font-bold text-[#18181B] text-xs uppercase tracking-wide"
                    style={{ backgroundColor: '#F4F4F5', borderBottom: '1px solid #E4E4E7' }}>
                    {item.title}
                  </td>
                </tr>
              );
            }
            const isUrl = item.value.startsWith('http');
            return (
              <tr key={i} style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td className="px-4 py-2.5 font-semibold text-[#52525B] align-top whitespace-nowrap"
                  style={{ width: '38%', backgroundColor: '#FAFAFA', fontSize: '12px' }}>
                  {item.field}
                </td>
                <td className="px-4 py-2.5 text-[#18181B] break-words" style={{ fontSize: '12px' }}>
                  {isUrl ? (
                    <a href={item.value} target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 underline underline-offset-2 hover:text-blue-800 break-all text-xs">
                      {item.value}
                    </a>
                  ) : (
                    item.value
                  )}
                </td>
              </tr>
            );
          })}
          {grouped.length === 0 && (
            <tr>
              <td colSpan={2} className="px-4 py-6 text-center text-[#A1A1AA] text-sm">
                No structured content found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Lead Detail View ─────────────────────────────────────────────────────────

function LeadDetailView({ leadId, onBack }: { leadId: number; onBack: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead-detail', leadId],
    queryFn: () => dashboardApi.leadDetail(leadId),
    staleTime: 5 * 60 * 1000,
  });

  const lead = data?.data;

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-[#71717A] hover:text-[#18181B] mb-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Leads
      </button>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-10 rounded-lg bg-[#F4F4F5] animate-pulse" />)}
          <div className="h-48 rounded-lg bg-[#F4F4F5] animate-pulse" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load email details
        </div>
      )}

      {lead && (
        <>
          {/* Subject */}
          <div className="rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Subject</p>
            <p className="text-sm font-bold text-[#18181B]">{lead.subject || 'No subject'}</p>
          </div>

          {/* Meta row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">From</p>
              <p className="text-xs font-medium text-[#18181B] break-all">{lead.sender || '—'}</p>
            </div>
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Received</p>
              <p className="text-xs font-medium text-[#18181B]">{formatDateTime(lead.received_at)}</p>
            </div>
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Website</p>
              <p className="text-xs font-semibold text-[#18181B]">{lead.website || 'Unknown'}</p>
            </div>
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Source</p>
              <span className="inline-block text-[11px] font-bold bg-[#FFF7ED] text-[#C2410C] px-2 py-0.5 rounded mt-0.5">{lead.source || 'gmail'}</span>
            </div>
          </div>

          {/* Email body */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Message</p>
            {lead.body_html ? (
              /* Rich HTML email — render in sandboxed iframe exactly like Gmail */
              <div className="rounded-xl border border-[#E4E4E7] overflow-hidden" style={{ height: '500px' }}>
                <iframe
                  title="Email preview"
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block', backgroundColor: '#fff' }}
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>*{box-sizing:border-box}body{margin:0;padding:16px;font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.5}img{max-width:100%;height:auto}table{border-collapse:collapse;width:100%}td,th{padding:6px 10px;vertical-align:top}a{color:#1a73e8}p{margin:0 0 8px}</style></head><body>${lead.body_html}</body></html>`}
                />
              </div>
            ) : (lead.body || lead.snippet) ? (
              /* Plain text fallback — parse into table rows */
              <EmailBodyTable text={lead.body || lead.snippet || ''} />
            ) : (
              <div className="rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] px-4 py-6 text-center text-sm text-[#A1A1AA]">
                No message content — click <strong>Sync</strong> in Lead Generation to fetch email bodies.
              </div>
            )}
          </div>

          {/* Inbox link */}
          {lead.email_account && (
            <p className="text-xs text-[#A1A1AA] text-center">Inbox: {lead.email_account}</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Leads List View (filtered by period) ────────────────────────────────────

function LeadsListView({ period, label, onLeadClick }: {
  period: string; label: string; onLeadClick: (id: number) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['leads-list', period],
    queryFn: () => dashboardApi.leadsList(period),
    staleTime: 2 * 60 * 1000,
  });

  const leads: any[] = data?.data || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between pb-2 border-b border-[#F4F4F5]">
        <span className="text-xs text-[#71717A]">{isLoading ? 'Loading…' : `${leads.length} leads`}</span>
        <span className="text-xs font-semibold text-[#F97316]">{label}</span>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-lg bg-[#F4F4F5] animate-pulse" />)}
        </div>
      )}

      {!isLoading && leads.length === 0 && (
        <p className="py-8 text-center text-sm text-[#A1A1AA]">No leads for this period</p>
      )}

      {leads.map((lead: any) => (
        <div key={lead.id}
          className="rounded-lg border border-[#E4E4E7] px-3 py-2.5 cursor-pointer hover:bg-[#FAFAFA] hover:border-[#F97316] transition-all"
          onClick={() => onLeadClick(lead.id)}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-[#18181B] truncate flex-1">{lead.subject || 'No subject'}</p>
            <svg className="w-3.5 h-3.5 text-[#A1A1AA] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
          <p className="text-xs text-[#71717A] mt-0.5 truncate">{lead.sender || lead.email_account}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[11px] bg-[#FFF7ED] text-[#C2410C] font-bold px-1.5 py-0.5 rounded">{lead.source || 'gmail'}</span>
            <span className="text-[11px] text-[#A1A1AA]">{lead.website || 'Unknown'}</span>
            <span className="text-[11px] text-[#A1A1AA] ml-auto">{formatDateTime(lead.received_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Panel body ───────────────────────────────────────────────────────────────

function PanelBody({
  view,
  onNavigate,
  onPeriodChange,
  onAnchorChange,
}: {
  view: PanelView;
  onNavigate: (v: PanelView) => void;
  onPeriodChange?: (p: string) => void;
  onAnchorChange?: (a: string) => void;
}) {
  if (!view) return null;

  if (view.type === 'task-detail') {
    return (
      <TaskDetail
        task={view.task}
        onBack={() => onNavigate(view.from ?? null)}
      />
    );
  }

  if (view.type === 'leads-list') {
    return (
      <LeadsListView
        period={view.period}
        label={view.label}
        onLeadClick={(id) => onNavigate({ type: 'lead-detail', leadId: id, from: view })}
      />
    );
  }

  if (view.type === 'lead-detail') {
    return (
      <LeadDetailView
        leadId={view.leadId}
        onBack={() => onNavigate(view.from)}
      />
    );
  }

  if (view.type === 'leads') {
    const summary = view.stats?.summary || {};
    const recent = view.stats?.recent || [];
    const byWebsite = view.stats?.byWebsite || [];
    const maxW = Math.max(...byWebsite.map((r: any) => Number(r.total || 0)), 1);
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Today', value: summary.today || 0 },
            { label: 'This Month', value: summary.month || 0 },
            { label: 'This Year', value: summary.year || 0 },
            { label: 'Total', value: summary.total || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-4 py-3">
              <p className="text-2xl font-extrabold tabular-nums text-[#18181B]">{value}</p>
              <p className="text-xs font-semibold text-[#71717A] mt-0.5">{label}</p>
            </div>
          ))}
        </div>
        {byWebsite.length > 0 && (
          <div>
            <p className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider mb-3">By Website</p>
            <div className="space-y-3">
              {byWebsite.map((row: any) => (
                <div key={row.website}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-semibold text-[#52525B]">{row.website}</span>
                    <span className="text-xs font-bold text-[#18181B]">{row.total}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#E4E4E7] overflow-hidden">
                    <div className="h-full rounded-full bg-[#F97316]" style={{ width: `${Math.max(8, (Number(row.total) / maxW) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {recent.length > 0 && (
          <div>
            <p className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider mb-3">Recent Leads</p>
            <div className="space-y-3">
              {recent.map((lead: any) => (
                <div key={lead.id}
                  className="rounded-lg border border-[#E4E4E7] px-3 py-2.5 cursor-pointer hover:bg-[#FAFAFA] hover:border-[#F97316] transition-all"
                  onClick={() => onNavigate({ type: 'lead-detail', leadId: lead.id, from: view })}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-[#18181B] truncate flex-1">{lead.subject || 'No subject'}</p>
                    <svg className="w-3.5 h-3.5 text-[#A1A1AA] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <p className="text-xs text-[#71717A] mt-0.5 truncate">{lead.sender || lead.email_account}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] bg-[#FFF7ED] text-[#C2410C] font-bold px-1.5 py-0.5 rounded">{lead.source || 'gmail'}</span>
                    <span className="text-[11px] text-[#A1A1AA]">{lead.website || 'Unknown'}</span>
                    <span className="text-[11px] text-[#A1A1AA] ml-auto">{formatDateTime(lead.received_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view.type === 'completed') {
    const inputType = view.period === 'day' ? 'date' : view.period === 'month' ? 'month' : 'number';
    const inputValue = view.period === 'year' ? view.anchor.slice(0, 4) : view.period === 'month' ? view.anchor.slice(0, 7) : view.anchor;
    const grouped = view.tasks.reduce((acc: Record<string, any[]>, t: any) => {
      const key = formatAssigneeNames(t) || 'Unassigned';
      if (!acc[key]) acc[key] = [];
      acc[key].push(t);
      return acc;
    }, {});
    const groups = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    const totalEst = view.tasks.reduce((s, t) => s + minutesFor(t), 0);
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center pb-2 border-b border-[#F4F4F5]">
          <select value={view.period} onChange={e => onPeriodChange?.(e.target.value)}
            className="rounded-lg border border-[#E4E4E7] px-3 py-1.5 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]">
            <option value="day">Day</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
          <input type={inputType} value={inputValue}
            min={view.period === 'year' ? '2020' : undefined}
            max={view.period === 'year' ? '2100' : undefined}
            onChange={e => {
              const v = e.target.value;
              if (view.period === 'year') onAnchorChange?.(`${v || new Date().getFullYear()}-01-01`);
              else if (view.period === 'month') onAnchorChange?.(`${v}-01`);
              else onAnchorChange?.(v);
            }}
            className="rounded-lg border border-[#E4E4E7] px-3 py-1.5 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]"
          />
          <span className="text-xs text-[#71717A] ml-auto">{view.tasks.length} done · {formatTotalEstimate(totalEst)}</span>
        </div>
        {view.tasks.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#A1A1AA]">No completed tasks for this period</p>
        ) : (
          groups.map(([member, tasks]) => {
            const est = tasks.reduce((s, t) => s + minutesFor(t), 0);
            return (
              <div key={member}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-[#18181B]">{member}</p>
                  <span className="text-xs text-[#A1A1AA]">{tasks.length} done · {formatTotalEstimate(est)}</span>
                </div>
                <div className="space-y-2">
                  {tasks.map((t: any) => (
                    <div key={`${t.source}-${t.id}`}
                      className="rounded-lg border border-[#E4E4E7] px-3 py-2.5 cursor-pointer hover:bg-[#FAFAFA]"
                      onClick={() => onNavigate({ type: 'task-detail', task: t, from: view })}
                    >
                      <p className="text-sm font-semibold text-[#18181B]">{t.name}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <span className="text-[11px] text-[#A1A1AA]">{t.website_name || 'Tkxel'}</span>
                        <span className="text-[11px] text-[#A1A1AA]">Done {formatDateTime(t.completed_on)}</span>
                        {formatEstimate(t) && <span className="text-[11px] font-semibold text-[#52525B]">{formatEstimate(t)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  }

  // task-list or member-tasks
  const tasks = view.type === 'member-tasks' ? view.tasks : view.tasks;
  const totalEst = tasks.reduce((s: number, t: any) => s + minutesFor(t), 0);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between pb-2 border-b border-[#F4F4F5]">
        <span className="text-xs text-[#71717A]">{tasks.length} tasks</span>
        {totalEst > 0 && <span className="text-xs font-semibold text-[#52525B]">{formatTotalEstimate(totalEst)} estimated</span>}
      </div>
      {tasks.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#A1A1AA]">No tasks</p>
      ) : (
        tasks.map((t: any) => (
          <TaskRow
            key={`${t.source}-${t.id}`}
            task={t}
            onOpen={(task) => onNavigate({ type: 'task-detail', task, from: view })}
          />
        ))
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMember = (user as any)?.role === 'member';
  const teamMemberName = ((user as any)?.team_member_name || user?.username || '').trim();

  const [teamFilter, setTeamFilter] = React.useState('');
  const [websiteFilter, setWebsiteFilter] = React.useState('');
  const [reportPeriod, setReportPeriod] = React.useState('day');
  const [reportAnchor, setReportAnchor] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [panelView, setPanelView] = React.useState<PanelView>(null);


  const reportRange = React.useMemo(() => getPeriodRange(reportPeriod, reportAnchor), [reportPeriod, reportAnchor]);

  const dashboardParams: Record<string, string> | undefined = !isMember
    ? { ...(teamFilter ? { team: teamFilter } : {}), ...(websiteFilter ? { website_id: websiteFilter } : {}) }
    : undefined;

  const completedParams: Record<string, string> | undefined = !isMember
    ? { ...(dashboardParams || {}), from: reportRange.from, to: reportRange.to }
    : undefined;

  const { data: websitesData } = useQuery({ queryKey: ['settings', 'websites'], queryFn: settingsApi.websites.list, enabled: !isMember });
  const { data: statsData, isLoading: statsLoading } = useQuery({ queryKey: ['dashboard', 'stats', teamFilter, websiteFilter], queryFn: () => dashboardApi.stats(dashboardParams) });
  const { data: progressData, isLoading: progressLoading } = useQuery({ queryKey: ['dashboard', 'progress', teamFilter, websiteFilter], queryFn: () => dashboardApi.progressByPageType(dashboardParams) });
  const { data: workloadData, isLoading: workloadLoading } = useQuery({ queryKey: ['dashboard', 'workload', teamFilter, websiteFilter], queryFn: () => dashboardApi.teamWorkload(dashboardParams) });
  const { data: blockedData } = useQuery({ queryKey: ['dashboard', 'blocked', teamFilter, websiteFilter], queryFn: () => dashboardApi.blockedTasks(dashboardParams) });
  const { data: pendingData } = useQuery({ queryKey: ['dashboard', 'pending-tasks', teamFilter, websiteFilter, ''], queryFn: () => dashboardApi.pendingTasks(dashboardParams), enabled: !isMember });
  const { data: completedData, isLoading: completedLoading } = useQuery({ queryKey: ['dashboard', 'completed-tasks', teamFilter, websiteFilter, reportPeriod, reportRange.from, reportRange.to], queryFn: () => dashboardApi.completedTasks(completedParams), enabled: !isMember });
  const { data: leadStatsData, isLoading: leadStatsLoading } = useQuery({
    queryKey: ['dashboard', 'lead-stats'],
    queryFn: dashboardApi.leadStats,
    enabled: !isMember,
    refetchInterval: 5 * 60 * 1000, // auto-check every 5 min for new leads
  });
  const { data: googleStatusData, isLoading: googleStatusLoading } = useQuery({ queryKey: ['google', 'status'], queryFn: googleApi.status, enabled: !isMember });

  // ── New-lead detection & celebration ──────────────────────────────────────
  const prevLeadTotalRef = React.useRef<number | null>(null);
  const [celebration, setCelebration] = React.useState<{ lead: any | null } | null>(null);
  React.useEffect(() => {
    if (!leadStatsData) return;
    const current = Number(leadStatsData.summary?.total || 0);
    const prev = prevLeadTotalRef.current;
    if (prev !== null && current > prev) {
      const newestLead = (leadStatsData.recent || [])[0] || null;
      setCelebration({ lead: newestLead });
    }
    prevLeadTotalRef.current = current;
  }, [leadStatsData]);

  const syncMutation = useMutation({
    mutationFn: googleApi.sync,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dashboard', 'lead-stats'] }); qc.invalidateQueries({ queryKey: ['google', 'status'] }); toast.success('Leads synced'); },
    onError: (err: any) => toast.error(err.message || 'Lead sync failed'),
  });

  const stats = statsData?.combined;
  const blockedTasks = blockedData?.data || [];
  const workloadRows = workloadData?.data || [];
  const pendingTasks = pendingData?.data || [];
  const completedTasks = completedData?.data || [];
  const websites = websitesData?.data || [];
  const completionRate = stats ? getProgressPercent(stats.done, stats.total) : 0;

  const connected = Boolean(googleStatusData?.connected);
  const connection = googleStatusData?.connection;
  const leadSummary = leadStatsData?.summary || { today: 0, month: 0, year: 0, total: 0 };

  // Keep completed panel in sync with period changes
  React.useEffect(() => {
    if (!panelView || panelView.type !== 'completed') return;
    setPanelView({ type: 'completed', tasks: completedTasks, period: reportPeriod, anchor: reportAnchor });
  }, [completedTasks, reportPeriod, reportAnchor]);

  React.useEffect(() => {
    if (isMember) return;
    const params = new URLSearchParams(window.location.search);
    const gs = params.get('google');
    if (!gs) return;
    if (gs === 'connected') { toast.success('Google mailbox connected'); qc.invalidateQueries({ queryKey: ['dashboard', 'lead-stats'] }); qc.invalidateQueries({ queryKey: ['google', 'status'] }); }
    else { toast.error(`Google connection failed: ${(params.get('message') || gs).replace(/-/g, ' ')}`); }
    params.delete('google'); params.delete('message');
    window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`);
  }, [isMember, qc]);

  const panelTitle = React.useMemo(() => {
    if (!panelView) return '';
    if (panelView.type === 'task-detail') return 'Task Details';
    if (panelView.type === 'lead-detail') return 'Email Details';
    if (panelView.type === 'leads') return 'Lead Generation';
    if (panelView.type === 'leads-list') return `Leads — ${panelView.label}`;
    if (panelView.type === 'completed') return 'Completed Work';
    if (panelView.type === 'member-tasks') return `${panelView.member}'s Open Tasks`;
    if (panelView.type === 'task-list') return panelView.title;
    return '';
  }, [panelView]);

  const panelSubtitle = React.useMemo(() => {
    if (!panelView) return '';
    if (panelView.type === 'task-list') return panelView.subtitle;
    if (panelView.type === 'member-tasks') return `${panelView.tasks.length} open tasks`;
    return '';
  }, [panelView]);

  return (
    <div className="space-y-5 max-w-[1400px]">

      {/* Member summary */}
      {isMember && teamMemberName && <MyTasksSummary teamMemberName={teamMemberName} />}

      {/* Filter bar */}
      {!isMember && (
        <div className="bg-white rounded-xl px-5 py-3.5 flex flex-wrap items-center justify-between gap-3"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #E4E4E7' }}>
          <div>
            <h2 className="text-sm font-bold text-[#18181B]">SEO Dashboard</h2>
            <p className="text-xs text-[#71717A]">Filter by website and team</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={websiteFilter} onChange={e => { setWebsiteFilter(e.target.value); }}
              className="rounded-lg border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]">
              <option value="">All Websites</option>
              {websites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={teamFilter} onChange={e => { setTeamFilter(e.target.value); }}
              className="rounded-lg border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]">
              <option value="">All Teams</option>
              {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* ── Stat Cards ── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <StatCardSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard title="Total Tasks" value={stats?.total ?? 0} subtitle="All SEO tasks" iconBg="#EFF6FF"
            onClick={() => setPanelView({ type: 'task-list', title: 'All Tasks', subtitle: 'All SEO tasks combined', tasks: pendingTasks })}
            icon={<svg className="w-5 h-5" style={{ color: '#2563EB' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
          />
          <StatCard title="Completed" value={stats?.done ?? 0} subtitle={`${completionRate}% rate`} iconBg="#F0FDF4"
            onClick={() => setPanelView({ type: 'completed', tasks: completedTasks, period: reportPeriod, anchor: reportAnchor })}
            icon={<svg className="w-5 h-5" style={{ color: '#16A34A' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
          <StatCard title="In Progress" value={stats?.in_progress ?? 0} subtitle="Being worked on" iconBg="#EFF6FF"
            onClick={() => setPanelView({ type: 'task-list', title: 'In Progress', subtitle: 'Currently being worked on', tasks: pendingTasks.filter((t: any) => t.status === 'In Progress') })}
            icon={<svg className="w-5 h-5" style={{ color: '#2563EB' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
          />
          <StatCard title="Blocked" value={stats?.blocked ?? 0} subtitle="Need attention" iconBg="#FEF2F2" accent={(stats?.blocked ?? 0) > 0}
            onClick={() => setPanelView({ type: 'task-list', title: 'Blocked Tasks', subtitle: 'Require immediate attention', tasks: blockedTasks })}
            icon={<svg className="w-5 h-5" style={{ color: '#DC2626' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>}
          />
        </div>
      )}

      {/* ── Overall Progress + Lead strip ── */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Progress bar */}
          <div className="xl:col-span-2 bg-white rounded-xl p-5"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #E4E4E7' }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-[#18181B]">Overall SEO Progress</h3>
                <p className="text-xs text-[#71717A] mt-0.5">Sitewide + Page tasks combined</p>
              </div>
              <span className="text-3xl font-extrabold text-[#18181B] tabular-nums leading-none">{completionRate}%</span>
            </div>
            <StackedBar done={stats.done} inProgress={stats.in_progress} pending={stats.pending} blocked={stats.blocked} total={stats.total} />
            <div className="flex flex-wrap gap-4 mt-3">
              <LegendItem color="#16A34A" label="Done" count={stats.done} />
              <LegendItem color="#2563EB" label="In Progress" count={stats.in_progress} />
              <LegendItem color="#F97316" label="Pending" count={stats.pending} />
              <LegendItem color="#DC2626" label="Blocked" count={stats.blocked} />
            </div>
          </div>

          {/* Lead generation strip */}
          {!isMember && (
            <div
              className="bg-white rounded-xl p-5 flex flex-col justify-between cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #E4E4E7' }}
              onClick={() => setPanelView({ type: 'leads', stats: leadStatsData })}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold text-[#18181B]">Lead Generation</h3>
                  <p className="text-xs text-[#71717A] mt-0.5">
                    {connected ? `${connection?.email}` : 'Gmail not connected'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!googleStatusLoading && (
                    connected ? (
                      <span className="rounded-full bg-[#F0FDF4] border border-[#BBF7D0] px-2 py-0.5 text-[11px] font-bold text-[#15803D]">Connected</span>
                    ) : (
                      <button type="button" onClick={e => { e.stopPropagation(); window.location.href = googleApi.connectUrl(); }}
                        className="rounded-lg bg-[#18181B] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#27272A]">
                        Connect
                      </button>
                    )
                  )}
                  {connected && (
                    <button type="button" onClick={e => { e.stopPropagation(); syncMutation.mutate(); }}
                      disabled={syncMutation.isPending}
                      className="rounded-lg border border-[#E4E4E7] px-2.5 py-1.5 text-xs font-semibold text-[#52525B] hover:bg-[#F4F4F5] disabled:opacity-50">
                      {syncMutation.isPending ? 'Syncing…' : 'Sync'}
                    </button>
                  )}
                </div>
              </div>
              {leadStatsLoading ? (
                <div className="grid grid-cols-2 gap-2">
                  {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-[#F4F4F5] animate-pulse" />)}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Today', value: leadSummary.today, period: 'today' },
                    { label: 'Month', value: leadSummary.month, period: 'month' },
                    { label: 'Year', value: leadSummary.year, period: 'year' },
                    { label: 'Total', value: leadSummary.total, period: 'all' },
                  ].map(({ label, value, period }) => (
                    <button key={label} type="button"
                      onClick={e => { e.stopPropagation(); setPanelView({ type: 'leads-list', period, label: `${value} ${label}` }); }}
                      className="rounded-lg bg-[#FAFAFA] border border-[#F4F4F5] px-3 py-2 text-left hover:border-[#F97316] hover:bg-[#FFF7ED] active:scale-95 transition-all">
                      <p className="text-xl font-extrabold tabular-nums text-[#18181B]">{value}</p>
                      <p className="text-[11px] text-[#71717A]">{label}</p>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#A1A1AA] mt-2 text-right">Click for details →</p>
            </div>
          )}
        </div>
      )}

      {/* ── Page Type Progress + Team Workload ── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">

        {/* Progress by Page Type */}
        <div className="xl:col-span-3 bg-white rounded-xl overflow-hidden"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #E4E4E7' }}>
          <div className="px-5 py-3.5 border-b border-[#F4F4F5]">
            <h3 className="text-sm font-bold text-[#18181B]">Progress by Page Type</h3>
          </div>
          {progressLoading ? (
            <div className="p-5 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-[#F4F4F5] rounded animate-pulse" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: '#FAFAFA' }}>
                    <th className="text-left px-5 py-2.5 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Page Type</th>
                    <th className="text-center px-3 py-2.5 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Pages</th>
                    <th className="text-center px-3 py-2.5 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Done</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-bold text-[#71717A] uppercase tracking-wider w-36">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {(progressData?.data || []).map((row: any, i: number) => {
                    const pct = getProgressPercent(row.done_count, row.task_count);
                    return (
                      <tr key={row.id} className="border-t border-[#F4F4F5] hover:bg-[#F4F4F5] transition-colors" style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F4F5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#FFFFFF' : '#FAFAFA')}>
                        <td className="px-5 py-2.5 font-semibold text-[#18181B]">{row.name}</td>
                        <td className="px-3 py-2.5 text-center text-[#71717A] text-sm">{row.total_pages ?? 0}</td>
                        <td className="px-3 py-2.5 text-center text-sm"><span className="font-bold text-[#18181B]">{row.done_count ?? 0}</span><span className="text-[#A1A1AA]">/{row.task_count ?? 0}</span></td>
                        <td className="px-3 py-2.5"><ProgressBar value={pct} showLabel size="sm" color="green" /></td>
                      </tr>
                    );
                  })}
                  {(progressData?.data || []).length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-[#A1A1AA] text-sm">No page tasks yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Team Workload */}
        <div className="xl:col-span-2 bg-white rounded-xl overflow-hidden"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #E4E4E7' }}>
          <div className="px-5 py-3.5 border-b border-[#F4F4F5]">
            <h3 className="text-sm font-bold text-[#18181B]">Team Workload</h3>
            <p className="text-xs text-[#71717A] mt-0.5">Click member to view their tasks</p>
          </div>
          {workloadLoading ? (
            <div className="p-4 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-[#F4F4F5] rounded animate-pulse" />)}</div>
          ) : (
            <div className="divide-y divide-[#F4F4F5]">
              {workloadRows.map((row: any, i: number) => {
                const total = Number(row.total || 0);
                const done = Number(row.done || 0);
                const inProgress = Number(row.in_progress || 0);
                const pending = Number(row.pending || 0);
                const blocked = Number(row.blocked || 0);
                const open = Math.max(0, total - done);
                return (
                  <button key={`${row.assigned_to}-${i}`} type="button"
                    onClick={() => {
                      const memberTasks = pendingTasks.filter((t: any) =>
                        (formatAssigneeNames(t) || '').toLowerCase().includes((row.assigned_to || '').toLowerCase())
                      );
                      setPanelView({ type: 'member-tasks', member: row.assigned_to, tasks: memberTasks });
                    }}
                    className="block w-full px-5 py-3.5 text-left hover:bg-[#FAFAFA] transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#F97316' }}>
                          {row.assigned_to?.charAt(0).toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#18181B]">{row.assigned_to}</p>
                          <TeamBadge team={row.assigned_team} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#18181B]">{done}/{total}</p>
                        <p className="text-xs text-[#A1A1AA]">{open} open</p>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden flex" style={{ backgroundColor: '#E4E4E7' }}>
                      {total > 0 && done > 0 && <div style={{ width: `${(done / total) * 100}%`, backgroundColor: '#16A34A' }} />}
                      {total > 0 && inProgress > 0 && <div style={{ width: `${(inProgress / total) * 100}%`, backgroundColor: '#2563EB' }} />}
                      {total > 0 && pending > 0 && <div style={{ width: `${(pending / total) * 100}%`, backgroundColor: '#F97316' }} />}
                      {total > 0 && blocked > 0 && <div style={{ width: `${(blocked / total) * 100}%`, backgroundColor: '#DC2626' }} />}
                    </div>
                  </button>
                );
              })}
              {workloadRows.length === 0 && (
                <div className="px-5 py-8 text-center text-[#A1A1AA] text-sm">No assigned tasks yet</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Blocked alert (compact) ── */}
      {blockedTasks.length > 0 && (
        <div className="rounded-xl px-5 py-4 flex items-start gap-3"
          style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEE2E2' }}>
            <svg className="w-4 h-4" style={{ color: '#DC2626' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#991B1B]">
              {blockedTasks.length} Blocked {blockedTasks.length === 1 ? 'Task' : 'Tasks'} — Require Attention
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {blockedTasks.slice(0, 3).map((t: any, i: number) => (
                <button key={i} type="button"
                  onClick={() => setPanelView({ type: 'task-detail', task: t, from: { type: 'task-list', title: 'Blocked Tasks', subtitle: '', tasks: blockedTasks } })}
                  className="text-xs font-semibold text-[#B91C1C] bg-[#FEE2E2] rounded-lg px-2.5 py-1 hover:bg-[#FECACA] truncate max-w-[200px]">
                  {t.name}
                </button>
              ))}
              {blockedTasks.length > 3 && (
                <button type="button"
                  onClick={() => setPanelView({ type: 'task-list', title: 'Blocked Tasks', subtitle: 'Require immediate attention', tasks: blockedTasks })}
                  className="text-xs font-semibold text-[#B91C1C] bg-[#FEE2E2] rounded-lg px-2.5 py-1 hover:bg-[#FECACA]">
                  +{blockedTasks.length - 3} more
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── New Lead Celebration ── */}
      {celebration && !isMember && (
        <NewLeadCelebration
          lead={celebration.lead}
          onDone={() => setCelebration(null)}
        />
      )}

      {/* ── Slide Panel ── */}
      <SlidePanel
        open={panelView !== null}
        onClose={() => setPanelView(null)}
        title={panelTitle}
        subtitle={panelSubtitle}
      >
        <PanelBody
          view={panelView}
          onNavigate={setPanelView}
          onPeriodChange={(p) => {
            setReportPeriod(p);
            if (panelView?.type === 'completed') setPanelView({ ...panelView, period: p });
          }}
          onAnchorChange={(a) => {
            setReportAnchor(a);
            if (panelView?.type === 'completed') setPanelView({ ...panelView, anchor: a });
          }}
        />
      </SlidePanel>
    </div>
  );
}
